import { Route } from '@/models/route';
import mapboxgl, { Map, MapMouseEvent } from 'mapbox-gl';

const ROUTE_SELECTED_COLOR = '#C80720';
const ROUTE_UNSELECTED_COLOR = '#b2a0a2';

export default class MapRouteHelper {

    private static getLayerId(route: Route): string {
        return (route) ? 'route-' + route.id : undefined;
    }
    public static findFirstLabelLayerId(map: mapboxgl.Map): string {
        const layers = map.getStyle().layers;

        // Find the index of the first symbol label layer in the map style
        for (const actualLayer of layers) {
            if (actualLayer.type === 'symbol' && actualLayer.id.includes('label')) {
                return actualLayer.id;
            }
        }

        return '';
    }

    private static mapboxFirstLabelId: string;

    public static addAllRoutes(map: Map, routes: Route[]) {
        this.mapboxFirstLabelId = this.findFirstLabelLayerId(map);

        let selectedId = undefined;
        routes.forEach((route, index) => {
            const selected = index === 0;
            if (selected) {
                selectedId = route.id;
            }
            MapRouteHelper.addRoute(map, route, selected, this.mapboxFirstLabelId);
        });

        if (selectedId) {
            map.moveLayer('route-' + selectedId, this.mapboxFirstLabelId);
        }

    }

    private static addRoute(map: Map, route: Route, selected: boolean = false, beforeId?: string) {

        map.addLayer({
            id: 'route-' + route.id,
            type: 'line',
            source: {
                type: 'geojson',
                data: route.geojson_line
            },
            layout: {
                'line-join': 'bevel',
                'line-cap': 'butt'
            },
            paint: {
                'line-color': selected ? ROUTE_SELECTED_COLOR : ROUTE_UNSELECTED_COLOR,
                'line-width': 5
            }
        }, beforeId);

    }

    public static selectedRoute(map: Map, route: Route) {
        const routeLayerId = this.getLayerId(route);
        if (map && routeLayerId) {
            map.setPaintProperty(routeLayerId, 'line-color', ROUTE_SELECTED_COLOR);
            map.moveLayer(routeLayerId, this.mapboxFirstLabelId);
        }
    }

    public static unselectedRoute(map: Map, route: Route) {
        const routeLayerId = this.getLayerId(route);
        if (map && routeLayerId) {
            map.setPaintProperty(routeLayerId, 'line-color', ROUTE_UNSELECTED_COLOR);
            map.moveLayer(routeLayerId, this.mapboxFirstLabelId);
        }
    }

    public static showRoute(map: Map, route: Route) {
        const routeLayerId = this.getLayerId(route);
        if (map && routeLayerId) {
            map.setLayoutProperty(routeLayerId, 'visibility', 'visible');
        }
    }

    public static hideRoute(map: Map, route: Route) {
        const routeLayerId = this.getLayerId(route);
        if (map && routeLayerId) {
            map.setLayoutProperty(routeLayerId, 'visibility', 'none');
        }
    }

    public static addRouteBoxLayer(map: Map) {
        map.addLayer({
            id: 'routeBoxLayer',
            type: 'fill',
            // @ts-ignore
            source: 'routeBoxes',
            layout: {},
            paint: {
                'fill-color': '#868088',
                'fill-opacity': 0
            }
        });
    }

    public static focusRoute(map: Map, route: Route) {
        const routeBounds = MapRouteHelper.createBoundingBox(route);
        map.fitBounds(routeBounds, {
            padding: { top: 15, bottom: 220, left: 15, right: 15 },
            maxZoom: 13,
            speed: 0.5
        });
    }

    public static findRouteIdFor(map: Map, event: MapMouseEvent): number {
        const features = map.queryRenderedFeatures(event.point, { layers: ['routeBoxLayer'] });
        if (features.length < 1) {
            return;
        }

        const route = features[0];
        return route.id as number;
    }

    public static createBoundingBoxesGeoJSON(routes: Route[]): GeoJSON.Feature[] {
        const geoJsonBoxes = new Array<GeoJSON.Feature>();

        for (const route of routes) {
            const coordinates = route.geojson_line.geometry.coordinates;
            const boundaries = this.coordinatesToShape(coordinates);

            geoJsonBoxes.push({
                type: 'Feature',
                id: route.id,
                geometry: {
                    type: 'Polygon',
                    coordinates: [boundaries]
                },
                properties: {
                    title: route.title
                }
            });
        }

        return geoJsonBoxes;
    }

    public static createBoundingBox(route: Route) {
        return this.coordinatesToBounds(route.geojson_line.geometry.coordinates);
    }

    public static coordinatesToBounds(coordinates) {
        const initial = new mapboxgl.LngLatBounds(coordinates[0], coordinates[0]);
        const boundingBox = coordinates.reduce((bounds, coord) => bounds.extend(coord), initial);
        return boundingBox;
    }

    public static coordinatesToShape(coordinates): any[][] {
        const bounds = this.coordinatesToBounds(coordinates);

        return [
            [bounds._ne.lng, bounds._ne.lat],
            [bounds._sw.lng, bounds._ne.lat],
            [bounds._sw.lng, bounds._sw.lat],
            [bounds._ne.lng, bounds._sw.lat],
            [bounds._ne.lng, bounds._ne.lat]
        ];
    }
}
